import { useEffect } from 'react'

import { useFormWithSchema } from '@fortem-monorepo/react-spa'
import {
  Button,
  DatePicker,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormField,
  FormItem,
  FormLabel,
  Input,
  SubmitButton,
  Switch,
} from '@fortem-monorepo/react-ui'

import { addMappingSchema } from '@/components/forms/AddMappingForm/AddMappingSchema'
import { useOpsContext } from '@/pages/operations/ops-context.context'
import { useAddMappingMutation } from '@/react-query/mutations/mappings'

export interface AddMappingModalProps {}

export const AddMappingModal: React.FC<AddMappingModalProps> = () => {
  const { newMapping, setNewMapping } = useOpsContext()

  const form = useFormWithSchema(
    {
      mode: 'all',
      // @ts-ignore
      values: {
        ...newMapping,
      },
    },
    addMappingSchema,
  )

  const {
    trigger,
    formState: { isSubmitting, isValid },
  } = form

  const addMappingMutation = useAddMappingMutation()

  const submitHandler = form.handleSubmit(async (data) => {
    if (isValid) {
      // @ts-ignore
      const { agentIdDisabled, from_date, ...rest } = data

      // TODO: Will Sambells - this cast is a bit hacky, look at this
      await addMappingMutation.mutateAsync({
        from_date: from_date || (null as unknown as undefined),
        ...rest,
      })

      setNewMapping(null)
    }
  })

  useEffect(() => {
    trigger()
  }, [trigger, newMapping])

  return (
    <Dialog
      open={newMapping !== null}
      onOpenChange={(e) => {
        if (!e) {
          setNewMapping(null)
          form.reset()
        }
      }}
    >
      <DialogContent className="sm:max-w-[425px]">
        <Form {...form}>
          <form onSubmit={submitHandler}>
            <DialogHeader>
              <DialogTitle>
                Add
                {newMapping?.agentIdDisabled && (
                  <>
                    {' '}
                    <span className={'font-extrabold tracking-wider'}>
                      {newMapping.is_agent_id ? 'Custodian' : 'Bank'}
                    </span>
                  </>
                )}{' '}
                Mapping
              </DialogTitle>
              <DialogDescription>{newMapping?.structurename}</DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <FormField
                control={form.control}
                name="is_agent_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Agent ID?</FormLabel>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={newMapping?.agentIdDisabled || isSubmitting}
                    />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="structurename"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Structure Name</FormLabel>
                    <Input
                      {...field}
                      value={field.value}
                      disabled={!!newMapping?.structurename || isSubmitting}
                    />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="provider"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Provider</FormLabel>
                    <Input {...field} disabled={isSubmitting} />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="external_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>External ID</FormLabel>
                    <Input {...field} disabled={isSubmitting} />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="from_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>From Date</FormLabel>
                    <DatePicker
                      buttonDisabled={isSubmitting}
                      className={'w-full!'}
                      selected={field.value ? new Date(field.value) : undefined}
                      onSelect={(date, e) => field.onChange(date?.toLocaleDateString('en-CA'), e)}
                      disabled={(date) => date > new Date() || date < new Date('1900-01-01')}
                    />
                  </FormItem>
                )}
              />
            </div>
            <DialogFooter>
              <Button
                onClick={() => {
                  setNewMapping(null)
                }}
                variant={'destructive'}
              >
                Cancel
              </Button>
              <SubmitButton
                loading={form.formState.isSubmitting}
                disabled={isSubmitting || !isValid}
              >
                Save changes
              </SubmitButton>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
